import React, { useState, useEffect } from 'react';
import { X, Send, Users, AlertCircle, MessageSquare, LayoutTemplate, Loader2 } from 'lucide-react';
import { getWhatsappTemplates } from '../../api/services/noticeApi';
import toast from 'react-hot-toast';
import RecipientSelector from '../Common/RecipientSelector';

/**
 * WhatsAppNoticeModal Component
 * 
 * Supports dynamic template selection and parameter mapping
 */
const WhatsAppNoticeModal = ({
  isOpen,
  onClose,
  exam,
  onSend,
  loading = false
}) => {
  const [createDraft, setCreateDraft] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState({});
  const [targetAudience, setTargetAudience] = useState(() => {
    const orgType = localStorage.getItem('organizationType')?.toUpperCase();
    return orgType === 'COLLEGE' ? 'both' : 'parent';
  });
  const [fetchingTemplates, setFetchingTemplates] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setFetchingTemplates(true);
    try {
      const res = await getWhatsappTemplates();
      if (res.success && Array.isArray(res.data)) {
        setTemplates(res.data);
        // Pre-select exam_schedule if it exists
        const defaultTpl = res.data.find(t => t.templateName === 'exam_schedule');
        if (defaultTpl) handleTemplateSelect(defaultTpl);
      }
    } catch (err) {
      toast.error("Failed to load WhatsApp templates");
    } finally {
      setFetchingTemplates(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Initialize params
    const initialParams = {};
    if (template.parameters) {
      template.parameters.forEach(p => {
        if (!p.mapping) {
          initialParams[p.paramKey] = p.default || '';
        }
      });
    }
    setTemplateParams(initialParams);
  };

  const handleParamChange = (key, value) => {
    setTemplateParams(prev => ({ ...prev, [key]: value }));
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleSend = () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }

    // Validate required params
    const missingParams = selectedTemplate.parameters
      ?.filter(p => p.required && !p.mapping && !templateParams[p.paramKey])
      .map(p => p.label);

    if (missingParams?.length > 0) {
      toast.error(`Please fill: ${missingParams.join(', ')}`);
      return;
    }

    onSend({
      sendWhatsapp: !createDraft,
      onlyPresentStudents: false,
      createDraft: createDraft,
      targetAudience: targetAudience,
      whatsappTemplate: selectedTemplate.templateName,
      whatsappParams: templateParams
    });
  };

  if (!isOpen) return null;

  // Get class names
  const classNames = exam?.classes?.map(c => 
    c.unitId?.unitName || c.unitName || 'Unknown'
  ).filter(Boolean).join(', ') || 'N/A';

  // Get subject count
  const subjectCount = exam?.subjects?.length || 0;

  // Get date range
  const examDates = exam?.subjects
    ?.map(s => s.examDate)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b));
  
  const startDate = examDates?.[0] 
    ? new Date(examDates[0]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'Not set';
  
  const endDate = examDates?.[examDates.length - 1]
    ? new Date(examDates[examDates.length - 1]).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'Not set';

  // Helper to get preview
  const getPreviewContent = () => {
    if (!selectedTemplate) return "Select a template to see preview...";
    
    // This is a simplified preview. In real world, it would replace {{1}}, {{2}} etc.
    // However, the backend handles the actual merging.
    return `Notice: ${exam?.examName}\nType: ${exam?.examType}\nDates: ${startDate} - ${endDate}`;
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all animate-in zoom-in-95 duration-200">
        
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <MessageSquare className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Dynamic Exam Notice</h3>
              <p className="text-sm text-gray-600 mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]">{exam?.examName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-full transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Exam Info Summary */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 grid grid-cols-2 gap-4 text-xs font-bold text-gray-500 uppercase tracking-widest">
            <div>
              <p className="mb-1 opacity-60">Exam Type</p>
              <p className="text-gray-900">{exam?.examType || 'N/A'}</p>
            </div>
            <div>
              <p className="mb-1 opacity-60">Schedule</p>
              <p className="text-gray-900 truncate">{startDate} - {endDate}</p>
            </div>
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <LayoutTemplate className="h-3.5 w-3.5 text-emerald-600" />
              WhatsApp Template
            </label>
            <div className="relative">
              {fetchingTemplates ? (
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                  <span className="text-xs text-gray-500 font-medium">Fetching available templates...</span>
                </div>
              ) : (
                <select
                  value={selectedTemplate?.templateName || ''}
                  onChange={(e) => {
                    const tpl = templates.find(t => t.templateName === e.target.value);
                    if (tpl) handleTemplateSelect(tpl);
                  }}
                  className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm font-bold shadow-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="" disabled>Select a Template</option>
                  {templates.map(t => (
                    <option key={t.templateName} value={t.templateName}>
                      {t.label || t.templateName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Dynamic Parameters */}
          {selectedTemplate && selectedTemplate.parameters?.filter(p => !p.mapping).length > 0 && (
            <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
               <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider">
                 <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                 Template Parameters
               </div>
               <div className="grid grid-cols-1 gap-4 bg-emerald-50/30 p-4 rounded-2xl border border-emerald-100">
                  {selectedTemplate.parameters
                    .filter(p => !p.mapping)
                    .map(param => (
                      <div key={param.paramKey}>
                        <label className="block text-[10px] font-bold text-emerald-700 uppercase mb-1.5">
                          {param.label} {param.required && <span className="text-red-500">*</span>}
                        </label>
                        {param.type === 'textarea' ? (
                          <textarea
                            value={templateParams[param.paramKey] || ''}
                            onChange={(e) => handleParamChange(param.paramKey, e.target.value)}
                            className="w-full p-3 bg-white border border-emerald-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all resize-none"
                            placeholder={`Enter ${param.label.toLowerCase()}...`}
                            rows={3}
                          />
                        ) : (
                          <input
                            type={param.type === 'date' ? 'date' : 'text'}
                            value={templateParams[param.paramKey] || ''}
                            onChange={(e) => handleParamChange(param.paramKey, e.target.value)}
                            className="w-full p-3 bg-white border border-emerald-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                            placeholder={`Enter ${param.label.toLowerCase()}...`}
                          />
                        )}
                      </div>
                    ))
                  }
               </div>
            </div>
          )}

          {/* Recipient Selector */}
          {!createDraft && (
            <div className="animate-in slide-in-from-top-2 duration-300">
              <RecipientSelector
                value={targetAudience}
                onChange={setTargetAudience}
                label="Message Recipients"
                helperText="Specify the primary audience for this exam notice"
              />
            </div>
          )}

          {/* Draft Toggle */}
          <button
            type="button"
            onClick={() => setCreateDraft(!createDraft)}
            className={`w-full p-4 rounded-2xl border-2 transition-all text-left flex items-start gap-4 ${
              createDraft 
                ? 'bg-blue-50 border-blue-500 shadow-md' 
                : 'bg-white border-gray-100 hover:border-gray-200'
            }`}
          >
            <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
              createDraft ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
            }`}>
              {createDraft && <div className="w-2 h-2 bg-white rounded-full"></div>}
            </div>
            <div>
              <p className={`text-sm font-bold ${createDraft ? 'text-blue-900' : 'text-gray-900'}`}>Create Draft Only</p>
              <p className={`text-xs mt-0.5 ${createDraft ? 'text-blue-700' : 'text-gray-500'}`}>Notice will be created in student dashboard but no WhatsApp will be sent.</p>
            </div>
          </button>

          {/* Warning */}
          {!createDraft && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-[11px] text-amber-800 font-medium leading-relaxed">
                Notice will be sent to parents/students of <strong>{classNames}</strong>. 
                Please ensure all parameters are correct before blasting.
              </p>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="p-6 border-t border-gray-100 bg-gray-50/80 backdrop-blur rounded-b-2xl flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 h-12 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-200 transition-all active:scale-95 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={loading || fetchingTemplates}
            className="flex-[2] h-12 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-sm font-black uppercase tracking-widest shadow-lg shadow-emerald-200 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>{createDraft ? 'Create Draft' : 'Send Blast'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppNoticeModal;
